import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Dancer, DancerInput, DancerStatus } from '@pirouette/shared';
import { dancerInputSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';
import { queryKeys } from '@/lib/queryClient';

export interface DancerListQuery {
  search?: string | undefined;
  status?: DancerStatus | 'all' | undefined;
  familyId?: string | undefined;
  costumeDueOnly?: boolean | undefined;
  paymentDueOnly?: boolean | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useDancers(query: DancerListQuery = {}) {
  return useQuery({
    queryKey: [...queryKeys.dancers(query.familyId), query],
    queryFn: async (): Promise<{ rows: Dancer[]; total: number }> => {
      let q = supabase
        .from('dancers')
        .select('*', { count: 'exact' })
        .is('deleted_at', null)
        .order('last_name', { ascending: true })
        .order('first_name', { ascending: true });

      if (query.familyId) q = q.eq('family_id', query.familyId);
      if (query.status && query.status !== 'all') q = q.eq('status', query.status);
      if (query.costumeDueOnly) q = q.eq('costume_due', true);
      if (query.paymentDueOnly) q = q.eq('payment_due', true);

      if (query.search && query.search.trim() !== '') {
        const term = `%${query.search.trim()}%`;
        q = q.or(`first_name.ilike.${term},last_name.ilike.${term},preferred_name.ilike.${term}`);
      }
      if (query.limit !== undefined) {
        q = q.range(query.offset ?? 0, (query.offset ?? 0) + query.limit - 1);
      }
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: (data ?? []) as Dancer[], total: count ?? 0 };
    },
  });
}

export function useCreateDancer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DancerInput): Promise<Dancer> => {
      const parsed = dancerInputSchema.parse(input);
      const { data, error } = await supabase
        .from('dancers')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data as Dancer;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dancers() });
    },
  });
}

export function useUpdateDancer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<DancerInput>): Promise<Dancer> => {
      const { id, ...rest } = input;
      const { data, error } = await supabase
        .from('dancers')
        .update(stripUndefined(rest))
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Dancer;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dancers() });
    },
  });
}

export function useSoftDeleteDancer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from('dancers')
        .update({ deleted_at: new Date().toISOString(), status: 'withdrawn' })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dancers() });
    },
  });
}
